import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { calcolaPremiInsegna } from '../../../utils/calcoloPremi';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';

// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);
const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

interface InsegnaCliente {
  id: number;
  codice: string;
  nome: string;
}

interface Scaglione {
  id?: number;
  scaglioneMin: string;
  scaglioneMax: string;
  percentuale: string;
  importoFisso: string;
}

interface Premio {
  id: number;
  tipo: string;
  descrizione: string;
  anno: number;
  metodoCalcolo: string;
  scaglioni: any[];
}

interface ModalData {
  id?: number;
  tipo: string;
  descrizione: string;
  metodoCalcolo: string;
  scaglioni: Scaglione[];
}

const TIPO_PREMIO_OPTIONS = [
  { value: 'PFA', label: 'PFA' },
  { value: 'VOLUMETRICO', label: 'Volumetrico' },
  { value: 'PROMOZIONALE', label: 'Promozionale' },
  { value: 'ALTRO', label: 'Altro' },
];

export default function PremiInsegnePage() {
  const annoCorrente = new Date().getFullYear();
  // Arrivando dalla scheda insegna: ?insegna=ID&anno=AAAA preseleziona i filtri
  const [searchParams] = useSearchParams();
  const insegnaDaUrl = Number(searchParams.get('insegna')) || null;
  const annoDaUrl = Number(searchParams.get('anno')) || null;
  const anniDisponibili = Array.from({ length: 6 }, (_, i) => annoCorrente - i);
  if (annoDaUrl && !anniDisponibili.includes(annoDaUrl)) {
    anniDisponibili.push(annoDaUrl);
    anniDisponibili.sort((a, b) => b - a);
  }

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [insegne, setInsegne] = useState<InsegnaCliente[]>([]);
  const [insegnaSelezionata, setInsegnaSelezionata] = useState<number | null>(insegnaDaUrl);
  const [annoSelezionato, setAnnoSelezionato] = useState<number>(annoDaUrl || annoCorrente);

  const [premi, setPremi] = useState<Premio[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({
    tipo: 'PFA',
    descrizione: '',
    metodoCalcolo: 'PROGRESSIVO',
    scaglioni: [],
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Simulatore
  const [fatturatoSimulato, setFatturatoSimulato] = useState('');
  const [risultatoSimulazione, setRisultatoSimulazione] = useState<any>(null);

  // Carica insegne
  useEffect(() => {
    const loadInsegne = async () => {
      if (!pronta) return;
      try {
        const p = new URLSearchParams();
        if (filterSocietaId) p.append('societaId', filterSocietaId);
        const data = await apiFetch<InsegnaCliente[]>(`/api/anagrafiche/insegne?${p}`);
        setInsegne(data);
        // Insegna non (più) in elenco, es. dopo un cambio società: si riparte dalla prima
        if (data.length > 0 && !data.some((i) => i.id === insegnaSelezionata)) {
          setInsegnaSelezionata(data[0].id);
        }
      } catch (err) {
        console.error('Errore caricamento insegne:', err);
      }
    };
    loadInsegne();
  }, [pronta, filterSocietaId]);

  // Carica premi quando cambiano insegna o anno
  const loadPremi = useCallback(async () => {
    if (!insegnaSelezionata || !pronta) return;

    try {
      setLoading(true);
      const p = new URLSearchParams();
      p.append('insegnaClienteId', insegnaSelezionata.toString());
      p.append('anno', annoSelezionato.toString());
      if (filterSocietaId) p.append('societaId', filterSocietaId);

      const data = await apiFetch<Premio[]>(`/api/anagrafiche/premi-insegna?${p}`);
      setPremi(data);
    } catch (err) {
      console.error('Errore caricamento premi:', err);
    } finally {
      setLoading(false);
    }
  }, [insegnaSelezionata, annoSelezionato, pronta, filterSocietaId]);

  useEffect(() => {
    loadPremi();
  }, [loadPremi]);

  const handleCreate = () => {
    setModalData({
      tipo: 'PFA',
      descrizione: '',
      metodoCalcolo: 'PROGRESSIVO',
      scaglioni: [{ scaglioneMin: '0', scaglioneMax: '', percentuale: '', importoFisso: '' }],
    });
    setError('');
    setShowModal(true);
  };

  const handleEdit = (premio: Premio) => {
    setModalData({
      id: premio.id,
      tipo: premio.tipo,
      descrizione: premio.descrizione,
      metodoCalcolo: premio.metodoCalcolo,
      scaglioni: premio.scaglioni.map((s) => ({
        id: s.id,
        scaglioneMin: s.scaglioneMin?.toString() || '0',
        scaglioneMax: s.scaglioneMax?.toString() || '',
        percentuale: s.percentuale?.toString() || '',
        importoFisso: s.importoFisso?.toString() || '',
      })),
    });
    setError('');
    setShowModal(true);
  };

  const handleDelete = async (premio: Premio) => {
    if (!window.confirm(`Confermi l'eliminazione del premio "${premio.descrizione}"?`)) {
      return;
    }

    try {
      await apiFetch(`/api/anagrafiche/premi-insegna/${premio.id}`, { method: 'DELETE' });
      loadPremi();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione del premio');
    }
  };

  const handleSave = async () => {
    if (!modalData.descrizione.trim()) {
      setError('Descrizione obbligatoria');
      return;
    }

    if (!filterSocietaId) {
      setError('Società non selezionata');
      return;
    }

    if (modalData.scaglioni.length === 0) {
      setError('Deve essere presente almeno uno scaglione');
      return;
    }

    // Validazione scaglioni
    for (const scaglione of modalData.scaglioni) {
      if (!scaglione.scaglioneMin) {
        setError('Ogni scaglione deve avere un valore "Da"');
        return;
      }
      if (!scaglione.percentuale && !scaglione.importoFisso) {
        setError('Ogni scaglione deve avere una percentuale o un importo fisso');
        return;
      }
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        societaId: parseInt(filterSocietaId, 10),
        insegnaClienteId: insegnaSelezionata,
        tipo: modalData.tipo,
        descrizione: modalData.descrizione.trim(),
        anno: annoSelezionato,
        metodoCalcolo: modalData.metodoCalcolo,
        scaglioni: modalData.scaglioni.map((s) => ({
          scaglioneMin: parseFloat(s.scaglioneMin) || 0,
          scaglioneMax: s.scaglioneMax ? parseFloat(s.scaglioneMax) : null,
          percentuale: s.percentuale ? parseFloat(s.percentuale) : null,
          importoFisso: s.importoFisso ? parseFloat(s.importoFisso) : null,
        })),
      };

      if (modalData.id) {
        await apiFetch(`/api/anagrafiche/premi-insegna/${modalData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch('/api/anagrafiche/premi-insegna', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      setShowModal(false);
      loadPremi();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio del premio');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicaAnno = async () => {
    if (!insegnaSelezionata) return;

    const annoDa = annoSelezionato - 1;
    if (!window.confirm(`Vuoi copiare la configurazione premi del ${annoDa} per il ${annoSelezionato}? I premi già esistenti per il ${annoSelezionato} non verranno sovrascritti.`)) {
      return;
    }

    try {
      const result = await apiFetch<{ duplicati: number; saltati: number }>('/api/anagrafiche/premi-insegna/duplica-anno', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insegnaClienteId: insegnaSelezionata,
          annoDa,
          annoA: annoSelezionato,
        }),
      });

      alert(`Duplicati: ${result.duplicati} premi. Saltati: ${result.saltati} (già presenti).`);
      loadPremi();
    } catch (err: any) {
      alert(err.message || 'Errore nella duplicazione dei premi');
    }
  };

  const aggiungiScaglione = () => {
    setModalData({
      ...modalData,
      scaglioni: [...modalData.scaglioni, { scaglioneMin: '', scaglioneMax: '', percentuale: '', importoFisso: '' }],
    });
  };

  const rimuoviScaglione = (index: number) => {
    setModalData({
      ...modalData,
      scaglioni: modalData.scaglioni.filter((_, i) => i !== index),
    });
  };

  const aggiornaScaglione = (index: number, field: keyof Scaglione, value: string) => {
    const nuoviScaglioni = [...modalData.scaglioni];
    nuoviScaglioni[index] = { ...nuoviScaglioni[index], [field]: value };

    // Se si compila percentuale, svuota importo fisso e viceversa
    if (field === 'percentuale' && value) {
      nuoviScaglioni[index].importoFisso = '';
    } else if (field === 'importoFisso' && value) {
      nuoviScaglioni[index].percentuale = '';
    }

    setModalData({ ...modalData, scaglioni: nuoviScaglioni });
  };

  const handleCalcolaSimulazione = () => {
    const fatturato = parseFloat(fatturatoSimulato);
    if (!fatturato || fatturato <= 0) {
      alert('Inserisci un fatturato valido');
      return;
    }

    const risultato = calcolaPremiInsegna(premi, fatturato);
    setRisultatoSimulazione(risultato);
  };

  // Verifica se esistono premi per anno precedente
  const [haPremiAnnoPrecedente, setHaPremiAnnoPrecedente] = useState(false);
  useEffect(() => {
    const checkPremiAnnoPrecedente = async () => {
      if (!insegnaSelezionata) return;
      try {
        const data = await apiFetch<Premio[]>(
          `/api/anagrafiche/premi-insegna?insegnaClienteId=${insegnaSelezionata}&anno=${annoSelezionato - 1}`
        );
        setHaPremiAnnoPrecedente(data.length > 0);
      } catch {
        setHaPremiAnnoPrecedente(false);
      }
    };
    checkPremiAnnoPrecedente();
  }, [insegnaSelezionata, annoSelezionato]);

  // Messaggio di cortesia se non c'è società selezionata
  if (!pronta) {
    return (
      <Layout>
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Premi Insegna</h2>
            <p className="bop-text-muted">Gestione dei premi per insegne clienti</p>
          </div>
        </div>
        <div className="bop-card">
          <div className="bop-empty-state">
            <h3 className="bop-premi-insegne__empty-title">Seleziona una società</h3>
            <p className="bop-empty-state__text">
              Seleziona una società dalla barra in alto per visualizzare i premi insegna
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Premi Insegna</h2>
          <p className="bop-text-muted">Gestione dei premi per insegne clienti</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bop-card bop-card__body bop-premi-insegne__toolbar-card">
        <div className="bop-premi-insegne__toolbar">
          {/* Filtro Società */}
          {!isUnica && (
            <div className="bop-premi-insegne__filter-field">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={filterSocietaId}
                onChange={(e) => setFilterSocietaId(e.target.value)}
              >
                <option value="">Tutte le società</option>
                {societaList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-premi-insegne__filter-field--wide">
            <label className="bop-label">Insegna</label>
            <select
              className="bop-select"
              value={insegnaSelezionata || ''}
              onChange={(e) => setInsegnaSelezionata(parseInt(e.target.value))}
            >
              {insegne.map((ins) => (
                <option key={ins.id} value={ins.id}>
                  {ins.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="bop-premi-insegne__filter-field">
            <label className="bop-label">Anno</label>
            <select
              className="bop-select"
              value={annoSelezionato}
              onChange={(e) => setAnnoSelezionato(parseInt(e.target.value))}
            >
              {anniDisponibili.map((anno) => (
                <option key={anno} value={anno}>
                  {anno}
                </option>
              ))}
            </select>
          </div>

          <div className="bop-premi-insegne__toolbar-actions">
            <button className="bop-btn bop-btn-primary" onClick={handleCreate}>
              <PlusIcon />
              Aggiungi premio
            </button>
            <button
              className="bop-btn bop-btn-secondary"
              onClick={handleDuplicaAnno}
              disabled={!haPremiAnnoPrecedente}
              title={haPremiAnnoPrecedente ? 'Duplica da anno precedente' : 'Nessun premio presente nell\'anno precedente'}
            >
              <CopyIcon />
              Duplica da anno precedente
            </button>
          </div>
        </div>
      </div>

      {/* Griglia premi */}
      <div className="bop-card bop-premi-insegne__grid-card">
        {loading ? (
          <div className="bop-premi-insegne__loading">
            <p className="bop-text-muted">Caricamento in corso...</p>
          </div>
        ) : premi.length === 0 ? (
          <div className="bop-empty-state">
            <h3 className="bop-premi-insegne__empty-title">Nessun premio configurato</h3>
            <p className="bop-empty-state__text">Inizia creando il primo premio per questa insegna</p>
          </div>
        ) : (
          <div className="bop-table-wrapper">
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Descrizione</th>
                  <th>Metodo</th>
                  <th>Scaglioni</th>
                  <th className="bop-premi-insegne__col-actions">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {premi.map((premio) => (
                  <tr key={premio.id}>
                    <td>
                      <strong>{premio.tipo}</strong>
                    </td>
                    <td>{premio.descrizione}</td>
                    <td>{premio.metodoCalcolo === 'PROGRESSIVO' ? 'Progressivo' : 'Flat'}</td>
                    <td>
                      <span className="bop-badge bop-badge--info">
                        {premio.scaglioni.length}
                      </span>
                    </td>
                    <td>
                      <div className="bop-flex">
                        <button className="bop-btn-icon" onClick={() => handleEdit(premio)} title="Modifica">
                          <EditIcon />
                        </button>
                        <button className="bop-btn-icon" onClick={() => handleDelete(premio)} title="Elimina">
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Simulatore */}
      {premi.length > 0 && (
        <div className="bop-card bop-premi-insegne__simulator">
          <h3 className="bop-premi-insegne__simulator-title">Simulazione calcolo premi</h3>
          <div className="bop-premi-insegne__simulator-warning">
            <p className="bop-premi-insegne__simulator-warning-text">
              ⚠️ Il calcolo è puramente indicativo. Il fatturato viene inserito manualmente perché il modulo Ciclo Attivo non è ancora disponibile. I dati inseriti non vengono salvati.
            </p>
          </div>

          <div className="bop-premi-insegne__simulator-row">
            <div className="bop-premi-insegne__filter-field--wide">
              <label className="bop-label">Fatturato esercizio (€)</label>
              <input
                type="number"
                className="bop-input"
                value={fatturatoSimulato}
                onChange={(e) => setFatturatoSimulato(e.target.value)}
                placeholder="Es. 150000"
              />
            </div>
            <button className="bop-btn bop-btn-primary" onClick={handleCalcolaSimulazione}>
              Calcola
            </button>
          </div>

          {risultatoSimulazione && (
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Descrizione</th>
                    <th>Metodo</th>
                    <th>Dettaglio scaglioni</th>
                    <th className="bop-text-right">Importo calcolato</th>
                  </tr>
                </thead>
                <tbody>
                  {risultatoSimulazione.premi.map((p: any, idx: number) => (
                    <tr key={idx}>
                      <td>{p.tipo}</td>
                      <td>{p.descrizione}</td>
                      <td>{p.metodoCalcolo === 'PROGRESSIVO' ? 'Progressivo' : 'Flat'}</td>
                      <td className="bop-premi-insegne__detail-cell">
                        {p.dettaglioScaglioni.map((d: any, i: number) => (
                          <div key={i}>
                            {d.percentuale
                              ? `${d.quotaFatturato.toFixed(2)}€ × ${d.percentuale}% = ${d.importoCalcolato.toFixed(2)}€`
                              : `${d.importoFisso.toFixed(2)}€ fisso`}
                          </div>
                        ))}
                      </td>
                      <td className="bop-premi-insegne__amount-cell">{p.totale.toFixed(2)} €</td>
                    </tr>
                  ))}
                  <tr className="bop-premi-insegne__total-row">
                    <td colSpan={4} className="bop-text-right">
                      TOTALE GENERALE
                    </td>
                    <td className="bop-text-right">{risultatoSimulazione.totaleGenerale.toFixed(2)} €</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modale Crea/Modifica */}
      {showModal && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-premi-insegne__modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{modalData.id ? 'Modifica Premio' : 'Nuovo Premio'}</h3>
              <button className="bop-btn-icon" onClick={() => setShowModal(false)}>
                <XIcon />
              </button>
            </div>

            <div className="bop-modal__body">
              {error && (
                <div className="bop-premi-insegne__modal-error">
                  {error}
                </div>
              )}

              {/* Società - visibile solo se non è unica */}
              {!isUnica && (
                <div className="bop-form-group bop-premi-insegne__form-section">
                  <label className="bop-label">
                    Società <span className="bop-text-danger">*</span>
                  </label>
                  <select
                    className="bop-select"
                    value={filterSocietaId}
                    onChange={(e) => setFilterSocietaId(e.target.value)}
                    disabled={!!modalData.id}
                  >
                    <option value="">Seleziona società...</option>
                    {societaList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome}
                      </option>
                    ))}
                  </select>
                  {modalData.id && (
                    <small className="bop-premi-insegne__hint">
                      La società non può essere modificata
                    </small>
                  )}
                </div>
              )}

              <div className="bop-premi-insegne__modal-grid">
                <div className="bop-form-group">
                  <label className="bop-label">
                    Tipo <span className="bop-text-danger">*</span>
                  </label>
                  <select
                    className="bop-select"
                    value={modalData.tipo}
                    onChange={(e) => setModalData({ ...modalData, tipo: e.target.value })}
                  >
                    {TIPO_PREMIO_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">
                    Anno
                  </label>
                  <input type="text" className="bop-input" value={annoSelezionato} disabled />
                </div>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">
                  Descrizione <span className="bop-text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="bop-input"
                  value={modalData.descrizione}
                  onChange={(e) => setModalData({ ...modalData, descrizione: e.target.value })}
                  placeholder="Es. Premio fatturato annuo"
                />
              </div>

              <div className="bop-form-group">
                <label className="bop-label">
                  Metodo di calcolo <span className="bop-text-danger">*</span>
                </label>
                <div className="bop-premi-insegne__radio-group">
                  <label className="bop-premi-insegne__radio-label">
                    <input
                      type="radio"
                      value="PROGRESSIVO"
                      checked={modalData.metodoCalcolo === 'PROGRESSIVO'}
                      onChange={(e) => setModalData({ ...modalData, metodoCalcolo: e.target.value })}
                    />
                    <span>
                      Progressivo <small className="bop-text-muted">(come IRPEF, % su quota scaglione)</small>
                    </span>
                  </label>
                  <label className="bop-premi-insegne__radio-label">
                    <input
                      type="radio"
                      value="FLAT"
                      checked={modalData.metodoCalcolo === 'FLAT'}
                      onChange={(e) => setModalData({ ...modalData, metodoCalcolo: e.target.value })}
                    />
                    <span>
                      Flat <small className="bop-text-muted">(% scaglione su intero fatturato)</small>
                    </span>
                  </label>
                </div>
              </div>

              <div className="bop-premi-insegne__scaglioni-section">
                <h4 className="bop-premi-insegne__scaglioni-title">Scaglioni</h4>

                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Da (€)</th>
                        <th>A (€)</th>
                        <th>%</th>
                        <th>Importo fisso (€)</th>
                        <th className="bop-premi-insegne__col-actions--narrow"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalData.scaglioni.map((scaglione, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="number"
                              className="bop-input"
                              value={scaglione.scaglioneMin}
                              onChange={(e) => aggiornaScaglione(index, 'scaglioneMin', e.target.value)}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="bop-input"
                              value={scaglione.scaglioneMax}
                              onChange={(e) => aggiornaScaglione(index, 'scaglioneMax', e.target.value)}
                              placeholder="illimitato"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              className="bop-input"
                              value={scaglione.percentuale}
                              onChange={(e) => aggiornaScaglione(index, 'percentuale', e.target.value)}
                              placeholder="%"
                              disabled={!!scaglione.importoFisso}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              className="bop-input"
                              value={scaglione.importoFisso}
                              onChange={(e) => aggiornaScaglione(index, 'importoFisso', e.target.value)}
                              placeholder="€"
                              disabled={!!scaglione.percentuale}
                            />
                          </td>
                          <td>
                            <button
                              className="bop-btn-icon"
                              onClick={() => rimuoviScaglione(index)}
                              title="Rimuovi scaglione"
                            >
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button className="bop-btn bop-btn-secondary bop-premi-insegne__add-scaglione" onClick={aggiungiScaglione}>
                  <PlusIcon />
                  Aggiungi scaglione
                </button>
              </div>
            </div>

            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Annulla
              </button>
              <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
